import { Form, FormFieldset } from '@app/components/various/Form';
import { Link } from '@app/components/various/Link';
import { DEFAULT_LANGUAGES_CODES } from '@app/constants/translation';
import { fetchCmsNavigationRequest, getIsCmsNavigationLoading } from '@app/ducks';
import { externalLinks, paths } from '@app/paths';
import { useTranslations } from '@app/services/hooks/translations/useTranslations';
import { useUpdateTranslations } from '@app/services/hooks/translations/useUpdateTranslations';
import { useTranslationsConfig } from '@app/utils/hooks';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Translations as TranslationsNs } from '@typings';
import { mapObjIndexed, pick, without } from 'ramda';
import React from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { SettingsLayout } from '../SettingsLayout';

import { NavigationItemsTable } from './TranslationsContent/NavigationItemsTable';
import { PageContentTable } from './TranslationsContent/PageContentTable';

export const TranslationsSettings = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['common', 'translations']);
  const isLoadingNavigation = useSelector(getIsCmsNavigationLoading);
  const { data: translations, isPending } = useTranslations();
  const { mutate, isPending: isUpdating } = useUpdateTranslations();
  const { supportedLanguages, areMultipleLanguagesAvailable } = useTranslationsConfig();
  const formMethods = useForm<TranslationsNs.TranslationsDTOAdditional>();
  const { reset } = formMethods;
  const isLoading = isLoadingNavigation || isPending;

  React.useEffect(() => {
    if (!isDefined(translations)) {
      return;
    }

    const additionalLanguagesCodes = without(
      DEFAULT_LANGUAGES_CODES,
      supportedLanguages.map(({ code }) => code),
    );

    formMethods.reset({
      labels: mapObjIndexed(value => pick(additionalLanguagesCodes, value), translations.labels),
      navigationItems: mapObjIndexed(value => {
        const values = pick(additionalLanguagesCodes, value);

        if (isEmpty(values)) {
          return additionalLanguagesCodes.reduce((acc, code) => ({ ...acc, [code]: '' }), {});
        }

        return values;
      }, translations.navigationItems),
    });
  }, [supportedLanguages, formMethods, translations]);

  const handleCancel = React.useCallback(() => {
    reset();
  }, [reset]);

  React.useEffect(() => {
    if (!areMultipleLanguagesAvailable) {
      return;
    }

    dispatch(fetchCmsNavigationRequest());
  }, [dispatch, areMultipleLanguagesAvailable]);

  const handleSubmit = (data: TranslationsNs.TranslationsDTOAdditional) => {
    mutate(data);
  };

  const canApplyTranslations = !isLoading && areMultipleLanguagesAvailable;
  const formName = 'TranslationsForm';

  return (
    <SettingsLayout
      activePath={paths.SETTINGS_TRANSLATIONS}
      isLoading={isUpdating}
      isDisabled={!canApplyTranslations}
      hasChanges={formMethods.formState.isDirty}
      onCancel={handleCancel}
      formName={formName}
    >
      <Form formMethods={formMethods} onSubmit={handleSubmit} id={formName}>
        <p className="mb-5 body-2">
          <Trans t={t} i18nKey="translations:enable_languages_info">
            Showroom languages can be enabled in Centra AMS → Showroom plugin settings.
            <Link className="ml-2" target="_blank" to={externalLinks.SUPPORT_TRANSLATIONS}>
              Read more
            </Link>
          </Trans>
        </p>
        {areMultipleLanguagesAvailable && (
          <FormFieldset isDisabled={isLoading || isUpdating} className="space-y-4">
            <PageContentTable isLoading={isLoading} />
            <NavigationItemsTable isLoading={isLoading} />
          </FormFieldset>
        )}
      </Form>
    </SettingsLayout>
  );
};
